package com.schoolconnect.app.feature.attendance

import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.repository.parentattendance.ParentAttendanceRepository
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.*

class ParentAttendanceViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val initialMonth = monthParts(0)
    private val _uiState = MutableStateFlow(
        ParentAttendanceUiState(
            children = initialChildren,
            monthLabel = monthLabel(initialMonth.first, initialMonth.second),
            calendarStartOffset = mondayFirstOffset(initialMonth.first, initialMonth.second),
            todayDay = currentDateParts().takeIf { it.year == initialMonth.first && it.month == initialMonth.second }?.day,
            isLoading = true
        )
    )
    val uiState: StateFlow<ParentAttendanceUiState> = _uiState.asStateFlow()

    init {
        observeGlobalSelection()
        loadSelectedChild()
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _uiState.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _uiState.value.selectedChildIndex) onEvent(ParentAttendanceUiEvent.ChildSelected(index))
            }
        }
    }

    fun onEvent(event: ParentAttendanceUiEvent) {
        when (event) {
            is ParentAttendanceUiEvent.ChildSelected -> {
                val index = event.index.coerceIn(0, _uiState.value.children.lastIndex.coerceAtLeast(0))
                _uiState.update {
                    it.copy(
                        selectedChildIndex = index,
                        attendancePercentage = 0,
                        presentDays = 0,
                        totalDays = 0,
                        absentDays = 0,
                        todayStatus = "—",
                        todayMarkedAt = "—",
                        calendarDays = emptyList(),
                        absenceHistory = emptyList(),
                        trend = emptyList(),
                        isLoading = true,
                        errorMessage = null
                    )
                }
                loadSelectedChild()
            }
            ParentAttendanceUiEvent.PreviousMonth -> changeMonth(-1)
            ParentAttendanceUiEvent.NextMonth -> changeMonth(1)
            ParentAttendanceUiEvent.Retry -> loadSelectedChild()
        }
    }

    private fun changeMonth(delta: Int) {
        val offset = _uiState.value.monthOffset + delta
        val (year, month) = monthParts(offset)
        _uiState.update {
            it.copy(
                monthOffset = offset,
                monthLabel = monthLabel(year, month),
                calendarStartOffset = mondayFirstOffset(year, month),
                todayDay = currentDateParts().takeIf { it.year == year && it.month == month }?.day,
                isLoading = true
            )
        }
        loadCalendar()
    }

    private fun loadSelectedChild() {
        val studentId = _uiState.value.children.getOrNull(_uiState.value.selectedChildIndex)?.id.orEmpty()
        if (studentId.isBlank()) { _uiState.update { it.copy(isLoading = false, errorMessage = "No linked child was found.") }; return }
        scope.launch {
            ParentAttendanceRepository.loadDetail(studentId).onSuccess(::applyDetail).onFailure { error -> _uiState.update { it.copy(errorMessage = error.message ?: "Unable to load attendance.") } }
            loadCalendar()
        }
    }

    private fun loadCalendar() {
        val studentId = _uiState.value.children.getOrNull(_uiState.value.selectedChildIndex)?.id.orEmpty()
        if (studentId.isBlank()) return
        val (year, month) = monthParts(_uiState.value.monthOffset)
        scope.launch {
            ParentAttendanceRepository.loadCalendar(studentId, year, month).onSuccess { applyCalendar(it, year, month) }.onFailure { error -> _uiState.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load attendance calendar.") } }
        }
    }

    private fun applyDetail(raw: JsonObject) {
        val counts = raw.firstObject("attendance_count", "attendanceCount")
            ?: raw.firstObject("analytics", "statistics", "summary")
        val history = raw.firstArray("absence_history", "absenceHistory", "history", "ledger").map { item -> item.jsonObject.let { ParentAttendanceHistoryRow(it.firstString("attendance_date", "date")?.toDisplayDate().orEmpty(), it.firstString("status", "attendance_status").orEmpty()) } }.filter { it.date.isNotBlank() }
        val trend = raw.firstArray("trend", "monthly_trend", "attendance_trend").map { item -> item.jsonObject.let { ParentAttendanceTrend(it.firstString("month", "label").orEmpty(), it.firstNumber("percentage", "attendance_percentage")?.toInt() ?: 0) } }.withCalendarYears()
        val presentDays = counts?.firstInt("present", "present_days", "attendance_present_days")
            ?: raw.firstInt("present_days", "attendance_present_days")
        val totalDays = counts?.firstInt("total_days", "attendance_total_days")
            ?: raw.firstInt("total_days", "attendance_total_days")
        val absentDays = counts?.firstInt("absent", "absent_days", "attendance_absent_days")
            ?: raw.firstInt("absent_days", "attendance_absent_days")
            ?: if (presentDays != null && totalDays != null) (totalDays - presentDays).coerceAtLeast(0) else null
        _uiState.update { state ->
            state.copy(
                attendancePercentage = (counts?.firstNumber("percentage", "attendance_percentage")
                    ?: raw.firstNumber("attendance_percentage", "percentage"))?.toInt() ?: 0,
                presentDays = presentDays ?: 0,
                totalDays = totalDays ?: 0,
                absentDays = absentDays ?: 0,
                todayStatus = raw.firstString("today_status", "today_attendance_status") ?: "—",
                todayMarkedAt = raw.firstString("today_marked_at", "marked_at") ?: "—",
                absenceHistory = history,
                trend = trend,
                errorMessage = null
            )
        }
    }

    private fun applyCalendar(raw: JsonObject, year: Int, month: Int) {
        val calendarItems = raw.firstArray("days", "calendar", "attendance")
        val days = calendarItems.mapNotNull { item ->
            val itemObject = item.jsonObject
            val day = itemObject.firstInt("day", "day_of_month") ?: itemObject.firstString("attendance_date", "date")?.takeLast(2)?.toIntOrNull() ?: return@mapNotNull null
            ParentAttendanceDay(day, itemObject.firstString("status")?.toParentStatus() ?: ParentAttendanceStatus.NONE)
        }
        val today = currentDateParts()
        val todayRecord = if (today.year == year && today.month == month) {
            calendarItems.firstOrNull { item ->
                val itemObject = item.jsonObject
                val day = itemObject.firstInt("day", "day_of_month") ?: itemObject.firstString("attendance_date", "date")?.takeLast(2)?.toIntOrNull()
                day == today.day
            }?.jsonObject
        } else null
        val todayStatus = todayRecord?.firstString("status")?.toParentStatus()?.displayLabel()
        val todayMarkedAt = todayRecord?.firstString("marked_at", "attendance_time", "marked_time")
        _uiState.update {
            it.copy(
                calendarDays = days,
                todayDay = today.takeIf { it.year == year && it.month == month }?.day,
                monthLabel = monthLabel(year, month),
                calendarStartOffset = mondayFirstOffset(year, month),
                todayStatus = todayStatus ?: it.todayStatus,
                todayMarkedAt = todayMarkedAt ?: it.todayMarkedAt,
                isLoading = false,
                errorMessage = null
            )
        }
    }

    fun clear() = scope.cancel()
}

private data class DateParts(val year: Int, val month: Int, val day: Int)

private fun currentDateParts(): DateParts {
    val parts = currentAppDateLabel().split(" ")
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").indexOf(parts.getOrNull(1)).let { it + 1 }
    return DateParts(parts.getOrNull(2)?.toIntOrNull() ?: 2026, month.coerceIn(1, 12), parts.firstOrNull()?.toIntOrNull() ?: 1)
}

private fun monthParts(offset: Int): Pair<Int, Int> { val current = currentDateParts(); val zeroBased = current.month - 1 + offset; val yearOffset = zeroBased.floorDiv(12); val monthIndex = zeroBased.mod(12); return current.year + yearOffset to monthIndex + 1 }
private fun monthLabel(year: Int, month: Int): String = listOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December")[month - 1] + " " + year
private fun List<ParentAttendanceTrend>.withCalendarYears(): List<ParentAttendanceTrend> {
    var inferredYear = currentDateParts().year
    var previousMonth = 0
    return map { item ->
        val parts = item.month.trim().split(Regex("\\s+"))
        val monthIndex = listOf("january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december")
            .indexOfFirst { month -> month.startsWith(parts.firstOrNull().orEmpty().lowercase()) }
            .plus(1)
        val suppliedYear = parts.firstNotNullOfOrNull { it.toIntOrNull() }
        if (suppliedYear != null) inferredYear = suppliedYear
        else if (monthIndex in 1..12 && previousMonth > monthIndex) inferredYear++
        if (monthIndex in 1..12) previousMonth = monthIndex
        val label = if (suppliedYear == null) "${item.month} $inferredYear" else item.month
        item.copy(month = label)
    }
}
private fun mondayFirstOffset(year: Int, month: Int): Int {
    // Gregorian weekday for the first day of the month, normalized to Monday = 0.
    val monthOffsets = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    val adjustedYear = if (month < 3) year - 1 else year
    val weekdaySundayFirst = (adjustedYear + adjustedYear / 4 - adjustedYear / 100 + adjustedYear / 400 + monthOffsets[month - 1] + 1) % 7
    return (weekdaySundayFirst + 6) % 7
}
private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { get(it)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }
private fun JsonObject.firstInt(vararg keys: String): Int? = firstString(*keys)?.toIntOrNull()
private fun JsonObject.firstNumber(vararg keys: String): Double? = firstString(*keys)?.toDoubleOrNull()
private fun JsonObject.firstArray(vararg keys: String): JsonArray = keys.firstNotNullOfOrNull { get(it)?.jsonArray } ?: JsonArray(emptyList())
private fun JsonObject.firstObject(vararg keys: String): JsonObject? = keys.firstNotNullOfOrNull { get(it)?.jsonObject }
private fun String.toParentStatus(): ParentAttendanceStatus = when (uppercase()) { "P", "PRESENT" -> ParentAttendanceStatus.PRESENT; "A", "ABSENT" -> ParentAttendanceStatus.ABSENT; "A-M", "ABSENT_MORNING" -> ParentAttendanceStatus.ABSENT_MORNING; "A-A", "ABSENT_AFTERNOON" -> ParentAttendanceStatus.ABSENT_AFTERNOON; "L", "LATE" -> ParentAttendanceStatus.LATE; "E", "EXCUSED" -> ParentAttendanceStatus.EXCUSED; "HOLIDAY" -> ParentAttendanceStatus.HOLIDAY; else -> ParentAttendanceStatus.NONE }
private fun ParentAttendanceStatus.displayLabel(): String = when (this) { ParentAttendanceStatus.PRESENT -> "Present"; ParentAttendanceStatus.ABSENT -> "Absent"; ParentAttendanceStatus.ABSENT_MORNING -> "Absent (Morning)"; ParentAttendanceStatus.ABSENT_AFTERNOON -> "Absent (Afternoon)"; ParentAttendanceStatus.LATE -> "Late"; ParentAttendanceStatus.EXCUSED -> "Excused"; ParentAttendanceStatus.HOLIDAY -> "Holiday"; ParentAttendanceStatus.NONE -> "—" }
private fun String.toDisplayDate(): String = take(10).split("-").let { parts -> if (parts.size == 3) "${parts[2]} ${listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[parts[1].toIntOrNull()?.minus(1) ?: 0]} ${parts[0]}" else this }
